import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FC, useEffect } from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Alert } from '../../../../shared/lib/common/Alert';
import { CustomController } from '../../../../shared/lib/common/CustomController';
import { BUTTON_TEXT_CANCEL, BUTTON_TEXT_SAVE, LABEL_CONFIRM_EMAIL, LABEL_NEW_EMAIL, LABEL_EDIT_EMAIL } from '../../../../shared/lib/constants';
import palette from '../../../../shared/lib/theme/palette';
import { AUTH_TOKEN, EDIT_EMAIL_POPUP_VERBIAGE, LINKS, MSG_PLEASE_CHECK_YOUR_EMAIL, PENDING_PROMO_CODE } from '../../app/constants';
import { useAppContext } from '../../app/contexts/AppContext';
import { EditEmailInput, EmailConfirmationPopupProps } from '../../app/interfaceTypes';
import { editAccountEmailValidationSchema } from '../../app/validationSchema';
import { useUpdateEmailMutation } from '../../generated';
import { CustomModal } from '../common/CustomModal';

export const EditEmail: FC<EmailConfirmationPopupProps> = ({ setIsEdit, isOpen, setIsOpen }) => {
  const { setIsAppUserLoggedIn, setCurrentUser, currentUser } = useAppContext();
  const navigate = useNavigate();
  const { email } = currentUser || {};
  const { borderLight } = palette;

  const resetFields = () => {
    reset({
      email: email ?? '',
      confirmEmail: ''
    });
  };

  const emailEditClose = () => {
    setIsOpen?.(false);
    resetFields();
  };

  const [updateEmail, { loading }] = useUpdateEmailMutation({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const {
        updateUser: { email }
      } = data || {};

      if (email) {
        emailEditClose();
        setIsEdit(true);
        setIsEdit(false);
        setIsAppUserLoggedIn(false);
        setCurrentUser(null);
        navigate(LINKS.LOGIN_LINK);
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(PENDING_PROMO_CODE);
        Alert.success(MSG_PLEASE_CHECK_YOUR_EMAIL);
      }
    }
  });

  const methods = useForm<EditEmailInput, any, EditEmailInput>({
    mode: 'all',
    resolver: yupResolver(editAccountEmailValidationSchema(email ?? '')),

    defaultValues: {
      email: email ?? '',
      confirmEmail: ''
    }
  });

  const { handleSubmit, setValue, reset } = methods;

  useEffect(() => {
    email && setValue('email', email);
  }, [email]);

  const onSubmit: SubmitHandler<EditEmailInput> = async (data) => {
    const { email } = data;
    await updateEmail({
      variables: {
        updateUserInput: {
          email
        }
      }
    });
  };

  return (
    <Box>
      <CustomModal maxWidth="500px" isOpen={isOpen || false} handleClose={emailEditClose}>
        <Box position="relative">
          <Typography variant="h6" sx={{ fontWeight: '700', color: 'blackOne', padding: '24px 0' }}>
            {LABEL_EDIT_EMAIL}
          </Typography>
        </Box>

        <FormProvider {...methods}>
          <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <CustomController controllerLabel={LABEL_NEW_EMAIL} controllerName="email" fieldType="email" />

            <CustomController controllerLabel={LABEL_CONFIRM_EMAIL} controllerName="confirmEmail" fieldType="email" />

            <Typography variant="subtitle2" mb={2} pl={1} pt={1}>
              {EDIT_EMAIL_POPUP_VERBIAGE}
            </Typography>

            <Box display="flex" width="100%" gap="10px" mt="24px" mb="8px">
              <Button fullWidth onClick={emailEditClose} variant="text" sx={{ border: `1px solid ${borderLight}` }}>
                {BUTTON_TEXT_CANCEL}
              </Button>
              <Button fullWidth type="submit" variant="contained" color="primary" disabled={loading} endIcon={loading && <CircularProgress size={20} />}>
                {BUTTON_TEXT_SAVE}
              </Button>
            </Box>
          </form>
        </FormProvider>
      </CustomModal>
    </Box>
  );
};
